import { FormControl, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { reimbursementDescriptionId, reimbursementCategoryId, ApprovalStatus } from 'app/models/Placement/PlacementMods';
import { CategoryDescriptionOptions, isCategoryDescriptionPayableDefaultTraveler, isTransportationCarAllowance } from './ReimbursementHelper';
import { useSelector } from 'react-redux';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import * as StatusDefaults from 'app/components/Placement/NextPlacementStatus/StatusDefaults';
import { selectCurrentModDetails } from 'store/redux-store/placement-mods/selectors';

export enum PayeeOptions {
  Traveler = '1',
  Vendor = '2',
}
export const Payable = ({ options, classes, isModEdit, useDefaultPayableValues, onDescriptionChange }: 
                        { options: any; classes: any; isModEdit: boolean,
                          useDefaultPayableValues: boolean;
                          onDescriptionChange?: (isDirty: boolean) => void;
                        }) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useFormContext();
  const payeeId = 'payeeId';
  const theme = useTheme();
  const initialDescriptionRef = useRef<any>(null);

  const payable: any = useWatch({ name: payeeId });
  const reimbursementCategoryIdValue: any = useWatch({ name: reimbursementCategoryId });
  const reimbursementCategoryDescriptionId: any = useWatch({ name: reimbursementDescriptionId });
  // if we are setting a default then this is not required for the user, nor should it be validated
  const overrideRequired = useDefaultPayableValues ?? false;
  const reimbursementDescriptionValue = useWatch<any>({ name: reimbursementDescriptionId });
  const placementStatus = useSelector(selectPlacementStatus);
  const isActiveStatus = StatusDefaults.isPositiveActiveStatus(placementStatus?.currentPlacementStatus);
  const isEditable = () => {
    if (isModEdit) {
      return ![
        CategoryDescriptionOptions.MiscellaneousCellPhoneStipend,
        CategoryDescriptionOptions.MiscellaneousCarAllowance,
        CategoryDescriptionOptions.MiscellaneousMealAllowance,
        CategoryDescriptionOptions.MiscellaneousShiftCompletionBonus
      ].includes(reimbursementCategoryDescriptionId);
    }
    return false;
  };
  const currentModDetails = useSelector(selectCurrentModDetails);
  const isApproved = currentModDetails?.approvalStatusId === ApprovalStatus.Approved;

  useEffect(() => {
    // Check if this is the first render or if the description has changed
    const isFirstRender = initialDescriptionRef.current === null;
    const hasDescriptionChanged = !isFirstRender && 
      initialDescriptionRef.current !== reimbursementCategoryDescriptionId;
    
    // Notify parent component when description changes (but not on first render)
    if (hasDescriptionChanged) {
      onDescriptionChange?.(true);
    }

    if (reimbursementCategoryDescriptionId) {
      if (isTransportationCarAllowance(reimbursementCategoryIdValue, reimbursementDescriptionValue) &&
          isActiveStatus && isModEdit && (isApproved || hasDescriptionChanged)) {
        setValue(payeeId, PayeeOptions.Traveler, { shouldDirty: true });
        clearErrors('initialPayable');
      }
      else if (isCategoryDescriptionPayableDefaultTraveler(reimbursementCategoryDescriptionId) && isDirty) {
        setValue(payeeId, PayeeOptions.Traveler);
        clearErrors('initialPayable');
      } 
      else if (!payable && useDefaultPayableValues) {
        setValue(payeeId, PayeeOptions.Traveler);
        clearErrors('initialPayable');
      }
    }
    initialDescriptionRef.current = reimbursementCategoryDescriptionId;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [reimbursementCategoryIdValue, reimbursementCategoryDescriptionId, isDirty, isActiveStatus, isModEdit]);
  const handleRadioChange = e => {
    setValue(payeeId, e.target.value);
    clearErrors('initialPayable');
  };
  return (
    <FormControl>
      <Typography component="span" style={{ color: `${theme.palette.components.typography.color}` }}>
        {t('placement.placementMods.payable')}
      </Typography>
      <Controller
        control={control}
        name={payeeId}
        rules={{
          required: isModEdit && !overrideRequired,
        }}
        render={({ field: { onChange, ...rest } }) => {
          return (
            <FormControl disabled={!isEditable()}>
              <RadioGroup
                row
                value={payable}
                name={payeeId}
                onChange={e => {
                  onChange(e);
                  handleRadioChange(e);
                }}
              >
                {options &&
                  options.map(option => (
                    <FormControlLabel
                      checked={!!payable && parseInt(payable.toString()) === parseInt(option.ID)}
                      key={option.ID}
                      value={option.ID}
                      control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                      label={option.Description}
                      className={classes.radioLabel}
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          );
        }}
      />
    </FormControl>
  );
};
